/**
 * Workflow Core Atoms - Base state management for workflows
 *
 * NO LOCAL TYPES - All types come from types/api.ts
 * Workflows are stored WITHOUT phases (phases are in separate atom)
 */

import { atom } from "jotai";
import type { WorkflowListItem } from "@/isomorphic/schemas/workflow";
import type { AtomStatus, AtomError } from "@/frontend/types/atoms";

// ============================================================================
// BASE ATOMS (Private)
// ============================================================================

/**
 * Internal state atom: All workflows (raw state)
 * Private - do not use directly
 */
const _workflowsStateAtom = atom<WorkflowListItem[]>([]);

/**
 * Internal state atom: Workflows as they exist in DB
 * Used by auto-save to detect changes
 * Updated after successful CREATE/UPDATE/DELETE operations
 */
export const _workflowsInDBAtom = atom<WorkflowListItem[]>([]);

/**
 * Internal atom: All workflows (pure state)
 *
 * This is a simple state atom. Side-effects (history, auto-save) are handled
 * by action atoms in actions/workflow-actions.ts
 *
 * Private - use workflowsAtom for read access
 */
export const _workflowsAtom = atom(
  (get) => get(_workflowsStateAtom),
  (get, set, newWorkflows: WorkflowListItem[]) => {
    set(_workflowsStateAtom, newWorkflows);
  },
);

/**
 * Internal atom: Workspace workflows (from API)
 * Private - use workspaceWorkflowsAtom for read access
 */
export const _workspaceWorkflowsAtom = atom<WorkflowListItem[]>([]);

// ============================================================================
// PUBLIC READ-ONLY ATOMS
// ============================================================================

/**
 * Public atom: All workflows (read-only)
 */
export const workflowsAtom = atom((get) => get(_workflowsAtom));

/**
 * Public atom: Workspace workflows (read-only)
 */
export const workspaceWorkflowsAtom = atom((get) => get(_workspaceWorkflowsAtom));

// ============================================================================
// CURRENT WORKFLOW ATOMS
// ============================================================================

/**
 * Current workflow ID
 */
export const currentWorkflowIdAtom = atom<string | null>(null);

/**
 * Current workflow (derived from currentWorkflowIdAtom)
 */
export const currentWorkflowAtom = atom((get) => {
  const id = get(currentWorkflowIdAtom);
  if (!id) return null;

  const workflows = get(_workflowsAtom);
  return workflows.find((w) => w.id === id) || null;
});

// ============================================================================
// STATUS & ERROR ATOMS
// ============================================================================

/**
 * Loading status for workflows
 */
export const workflowsStatusAtom = atom<AtomStatus>("init");

/**
 * Loading status for workspace workflows
 */
export const workspaceWorkflowsStatusAtom = atom<AtomStatus>("init");

/**
 * Loading status for current workflow
 */
export const currentWorkflowStatusAtom = atom<AtomStatus>("init");

/**
 * Error for workflows
 */
export const workflowsErrorAtom = atom<AtomError>(null);

/**
 * Error for workspace workflows
 */
export const workspaceWorkflowsErrorAtom = atom<AtomError>(null);

/**
 * Error for current workflow
 */
export const currentWorkflowErrorAtom = atom<AtomError>(null);
